import { supabase } from "@/lib/supabase-client";
import { nextSequentialNumber } from "@/lib/domain/numbering";
import { EstimateType } from "@/types/db/estimates";
import { FileMetadata } from "@/types/files";

export const estimateService = {
  async getEstimates(includeArchived = false): Promise<EstimateType[]> {
    const { data, error } = await supabase.rpc("get_estimates", { p_include_archived: includeArchived });
    if (error) throw Error(error.message);
    return (data as any[]) || [];
  },

  async getEstimateById(id: string): Promise<EstimateType | null> {
    const { data, error } = await supabase.from("estimates").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }
    return data as EstimateType;
  },

  async addEstimate(estimateData: {
    tenant_id: string;
    client_id: string;
    project_id: string;
    status: string;
    meta_data?: any;
  }): Promise<EstimateType | null> {
    const { data, error } = await supabase.from("estimates").insert([estimateData]).select().single();
    if (error) {
      console.error("Error adding Estimate:", error.message);
      throw new Error(error.message);
    }
    return data as EstimateType;
  },

  async getEstimatesByProjectId(project_id: string): Promise<EstimateType[]> {
    const { data, error } = await supabase
      .from("estimates")
      .select(
        `id,
        project_id,
        project: projects(name),
        created_at,
        status,
        meta_data`,
      )
      .eq("project_id", project_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(`Error fetching estimates by project ${project_id}\r\n${error.message}`);
    }

    return data as unknown as EstimateType[];
  },

  async getEstimatesByClientId(client_id: string): Promise<EstimateType[]> {
    const { data, error } = await supabase
      .from("estimates")
      .select(
        `id,
        project_id,
        client_id,
        project: projects(id, name),
        created_at,
        status,
        meta_data`,
      )
      .eq("client_id", client_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(`Error fetching estimates by client ${client_id}\r\n${error.message}`);
    }

    return data as unknown as EstimateType[];
  },

  async updateEstimate(id: string, patch: Partial<EstimateType>): Promise<EstimateType> {
    const { data, error } = await supabase.from("estimates").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as EstimateType;
  },

  async saveRowPatches(patches: Partial<EstimateType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("estimates").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async deleteEstimatesByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("estimates").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
  },

  // File management methods
  async uploadFile(estimateId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from estimate record
    const { data: tenantData, error: tenantError } = await supabase
      .from("estimates")
      .select("tenant_id")
      .eq("id", estimateId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get estimate tenant: ${tenantError?.message || "Estimate not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${estimateId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("estimates").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "estimates", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(estimateId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("estimates")
      .select("tenant_id")
      .eq("id", estimateId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get estimate tenant: ${tenantError?.message || "Estimate not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${estimateId}`;
    const { data: files, error: listError } = await supabase.storage.from("estimates").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list estimate files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "estimates", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(estimateId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("estimates")
      .select("tenant_id")
      .eq("id", estimateId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get estimate tenant: ${tenantError?.message || "Estimate not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${estimateId}`;
    const { data: files, error: listError } = await supabase.storage.from("estimates").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list estimate files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("estimates").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(estimateId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("estimates")
      .select("tenant_id")
      .eq("id", estimateId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get estimate tenant: ${tenantError?.message || "Estimate not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${estimateId}`;
    const { data: files, error: listError } = await supabase.storage.from("estimates").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list estimate files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("estimates").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "estimates",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  async getNextEstimateNumber(): Promise<string> {
    // Fetch all estimate numbers to find the highest and ensure uniqueness
    const { data, error } = await supabase.from("estimates").select("meta_data->estimate_number");

    if (error) {
      console.error("Error fetching estimates for number generation:", error.message);
      return "1000";
    }

    return nextSequentialNumber((data || []).map((estimate) => (estimate as any)?.estimate_number));
  },

  async getPendingEstimatesCount(): Promise<number> {
    const { count, error } = await supabase
      .from("estimates")
      .select("*", { count: "exact", head: true })
      .in("meta_data->>status", ["DRAFT", "SENT"]);

    if (error) {
      console.error("Error fetching pending estimates count:", error.message);
      return 0;
    }

    return count || 0;
  },
};
